import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, X, ChevronDown } from "lucide-react";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";
import { Sheet, SheetContent, SheetTitle } from "../components/ui/sheet";
import { Skeleton } from "../components/ui/skeleton";

const CONDITIONS = ["New / Unused", "Excellent", "Very Good", "Good", "Fair"];
const COLORS = ["Noir / Black", "Blanc / Cream", "Caramel / Gold", "Bordeaux", "Metallics"];
const MATERIALS = ["Caviar Leather", "Lambskin", "Box Calf", "Smooth Calfskin", "Coated Canvas", "Exotic Watersnake", "Matelassé Leather", "Grained Leather"];
const SORTS = [["newest", "Newest"], ["price_asc", "Price: Low to High"], ["price_desc", "Price: High to Low"]];

function FilterGroup({ title, options, selected, onToggle }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border-b border-linen py-5">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full">
        <span className="eyebrow">{title}</span>
        <ChevronDown className={`w-4 h-4 text-stonemuted transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4 space-y-2.5">
          {options.map((opt) => {
            const val = typeof opt === "object" ? opt.name : opt;
            const active = selected.includes(val);
            return (
              <label key={val} className="flex items-center gap-3 cursor-pointer group">
                <span className={`w-4 h-4 border flex items-center justify-center ${active ? "bg-pitch border-pitch" : "border-stonemuted/50 group-hover:border-pitch"}`}>
                  {active && <span className="w-1.5 h-1.5 bg-ivory" />}
                </span>
                <input type="checkbox" className="sr-only" checked={active} onChange={() => onToggle(val)}
                  data-testid={`filter-${title.toLowerCase()}-${val.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
                <span className={`text-sm ${active ? "text-pitch" : "text-charcoal/70"}`}>{val}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mobileFilters, setMobileFilters] = useState(false);

  useEffect(() => {
    api.get("/brands").then((r) => setBrands(r.data)).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const getMulti = (key) => (params.get(key) ? params.get(key).split(",") : []);
  const sort = params.get("sort") || "newest";

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    const p = {};
    ["search", "brand", "category", "condition", "color", "material", "availability", "sort", "new_arrival", "featured", "min_price", "max_price"].forEach((k) => {
      const v = params.get(k);
      if (v) p[k] = v;
    });
    p.limit = 48;
    try {
      const { data } = await api.get("/products", { params: p });
      setProducts(data.products);
      setTotal(data.total);
    } catch {
      setLoadError(true);
      setProducts([]);
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const toggleMulti = (key, value) => {
    const cur = getMulti(key);
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    const np = new URLSearchParams(params);
    next.length ? np.set(key, next.join(",")) : np.delete(key);
    setParams(np, { replace: true });
  };
  const setSingle = (key, value) => {
    const np = new URLSearchParams(params);
    value ? np.set(key, value) : np.delete(key);
    setParams(np, { replace: true });
  };
  const clearAll = () => setParams(new URLSearchParams(), { replace: true });

  const activeCount = ["brand", "category", "condition", "color", "material", "availability", "min_price", "max_price"]
    .reduce((n, k) => n + (params.get(k) ? params.get(k).split(",").length : 0), 0);

  const Filters = () => (
    <div>
      <FilterGroup title="Brand" options={brands} selected={getMulti("brand")} onToggle={(v) => toggleMulti("brand", v)} />
      <FilterGroup title="Category" options={categories} selected={getMulti("category")} onToggle={(v) => toggleMulti("category", v)} />
      <FilterGroup title="Condition" options={CONDITIONS} selected={getMulti("condition")} onToggle={(v) => toggleMulti("condition", v)} />
      <FilterGroup title="Colour" options={COLORS} selected={getMulti("color")} onToggle={(v) => toggleMulti("color", v)} />
      <FilterGroup title="Material" options={MATERIALS} selected={getMulti("material")} onToggle={(v) => toggleMulti("material", v)} />
      <FilterGroup title="Availability" options={["available", "sold"]} selected={getMulti("availability")} onToggle={(v) => setSingle("availability", params.get("availability") === v ? "" : v)} />
      <div className="py-5">
        <span className="eyebrow">Price (AUD)</span>
        <div className="flex items-center gap-2 mt-4">
          <input type="number" placeholder="Min" defaultValue={params.get("min_price") || ""}
            onBlur={(e) => setSingle("min_price", e.target.value)}
            className="w-full border border-linen bg-white px-3 py-2 text-sm outline-none focus:border-pitch" />
          <span className="text-stonemuted">—</span>
          <input type="number" placeholder="Max" defaultValue={params.get("max_price") || ""}
            onBlur={(e) => setSingle("max_price", e.target.value)}
            className="w-full border border-linen bg-white px-3 py-2 text-sm outline-none focus:border-pitch" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="luxy-container py-10 sm:py-14">
      <div className="mb-8">
        <p className="eyebrow mb-3">The Archive</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-pitch tracking-tight">
          {params.get("search") ? `Results for "${params.get("search")}"` : params.get("new_arrival") ? "New Arrivals" : "Shop All"}
        </h1>
      </div>

      <div className="flex items-center justify-between border-y border-linen py-3 mb-8">
        <button onClick={() => setMobileFilters(true)} className="lg:hidden inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em]" data-testid="mobile-filter-trigger">
          <SlidersHorizontal className="w-4 h-4" /> Filters {activeCount > 0 && `(${activeCount})`}
        </button>
        <span className="hidden lg:block text-sm text-stonemuted">{total} {total === 1 ? "piece" : "pieces"}</span>
        <div className="flex items-center gap-3">
          {activeCount > 0 && <button onClick={clearAll} className="text-xs text-stonemuted hover:text-vermilion hidden sm:inline">Clear all</button>}
          <div className="relative">
            <select value={sort} onChange={(e) => setSingle("sort", e.target.value)} data-testid="filter-sort-dropdown"
              className="appearance-none bg-transparent border border-linen pl-4 pr-9 py-2 text-xs font-mono uppercase tracking-[0.12em] outline-none focus:border-pitch cursor-pointer">
              {SORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-stonemuted" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block"><Filters /></aside>
        <div>
          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i}><Skeleton className="aspect-[4/5] w-full" /><Skeleton className="h-4 w-1/2 mt-4" /><Skeleton className="h-5 w-3/4 mt-2" /></div>
              ))}
            </div>
          ) : loadError ? (
            <div className="py-24 text-center" data-testid="shop-load-error">
              <p className="font-serif text-2xl text-pitch">We couldn't load the collection</p>
              <p className="text-sm text-stonemuted mt-2 max-w-md mx-auto">There was a problem reaching our catalogue. Please check your connection and try again.</p>
              <button onClick={fetchProducts} className="mt-6 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1">Retry</button>
            </div>
          ) : products.length === 0 ? (
            activeCount === 0 && !params.get("search") && !params.get("new_arrival") && !params.get("featured") ? (
              <div className="py-24 text-center" data-testid="shop-coming-soon">
                <p className="eyebrow mb-5">The Atelier</p>
                <p className="font-serif text-3xl sm:text-4xl text-pitch tracking-tight">The next collection is coming soon</p>
                <p className="text-sm text-charcoal/70 mt-5 max-w-lg mx-auto leading-relaxed">LUXYREIGN is currently curating its latest selection of pre-owned luxury pieces. Please check back shortly.</p>
                <Link to="/contact" className="inline-block mt-8 bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] px-8 py-4">Register Your Interest</Link>
              </div>
            ) : (
              <div className="py-24 text-center" data-testid="shop-empty-state">
                <p className="font-serif text-2xl text-pitch">No pieces match your selection</p>
                <p className="text-sm text-stonemuted mt-2">Try adjusting or clearing your filters.</p>
                <button onClick={clearAll} className="mt-6 text-xs font-mono uppercase tracking-[0.2em] border-b border-pitch pb-1">Clear Filters</button>
              </div>
            )
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12" data-testid="shop-product-grid">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>

      <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
        <SheetContent side="left" className="bg-ivory w-[88vw] sm:w-[380px] border-linen overflow-y-auto p-0">
          <SheetTitle className="sr-only">Filters</SheetTitle>
          <div className="flex items-center justify-between px-6 h-16 border-b border-linen sticky top-0 bg-ivory z-10">
            <span className="eyebrow">Filters</span>
            <button onClick={() => setMobileFilters(false)}><X className="w-5 h-5" /></button>
          </div>
          <div className="px-6"><Filters /></div>
          <div className="p-6 sticky bottom-0 bg-ivory border-t border-linen">
            <button onClick={() => setMobileFilters(false)} className="w-full bg-pitch text-ivory text-xs font-mono uppercase tracking-[0.2em] py-4">View {total} Results</button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
